import { ButtonGradientSecondaryToPrimary, Colors, Icon, Text } from '@amelio/ui-library';
import { useState } from 'react';

import type { ThematicMeetingStatus } from '@/types/thematic-meetings';

import { MeetingCompletionCard } from '@/components/MeetingCompletionCard/MeetingCompletionCard';
import { useAmelioTranslations } from '@/hooks';
import { useNavigate } from '@/hooks/utils/useNavigate';
import { RouteName } from '@/navigation/types';
import { useMeetingStatusActions } from '@/pages/ThematicMeetings/hooks/useMeetingStatusActions';
import { useSavePendingEdits } from '@/pages/ThematicMeetings/Plan/components/PendingSavesScope';

interface CompletionSectionProps {
    meetingId: string;
    status: ThematicMeetingStatus;
    dataTestId?: string;
}

export const CompletionSection = ({
    meetingId,
    status,
    dataTestId = 'thematic-meeting-completion',
}: CompletionSectionProps) => {
    const { t } = useAmelioTranslations();
    const navigate = useNavigate();
    const { markCompleted, reopen, isCompleting, isReopening } = useMeetingStatusActions();
    const savePendingEdits = useSavePendingEdits();
    const [isSavingEdits, setIsSavingEdits] = useState(false);

    const isCompleted = status === 'Completed';

    // Completing locks the plan's editors, so what they hold is saved first; a refused save leaves
    // the meeting open.
    const saveEditsFirst = async () => {
        setIsSavingEdits(true);
        try {
            return await savePendingEdits();
        } finally {
            setIsSavingEdits(false);
        }
    };

    const handleComplete = async () => {
        if ((await saveEditsFirst()) && (await markCompleted(meetingId))) {
            navigate({ path: RouteName.ThematicMeetings });
        }
    };

    return (
        <MeetingCompletionCard
            icon={{ name: 'support-handshake', color: Colors.PRIMARY }}
            title={
                isCompleted
                    ? t('thematicMeetings.plan.completedTitle')
                    : t('thematicMeetings.plan.completionTitle')
            }
            description={
                isCompleted
                    ? t('thematicMeetings.plan.completedDescription')
                    : t('thematicMeetings.plan.completionDescription')
            }
            className={'mt-24'}
            dataTestId={dataTestId}
        >
            {isCompleted ? (
                <>
                    <span
                        className={
                            'border-primary bg-primary-50 flex items-center gap-2 rounded-full border-2 px-8 py-3'
                        }
                        data-testid={'thematic-meeting-completed-badge'}
                    >
                        <Icon
                            name={'essential-tick-circle'}
                            size={'sm'}
                            color={Colors.PRIMARY_900}
                            ariaHidden
                        />
                        <Text
                            size={'small'}
                            weight={'bold'}
                            color={Colors.PRIMARY_900}
                        >
                            {t('thematicMeetings.status.completed')}
                        </Text>
                    </span>

                    <ButtonGradientSecondaryToPrimary
                        onClick={() => void reopen(meetingId)}
                        loading={isReopening}
                        shape={'pill'}
                        fitContent
                        dataTestId={'reopen-meeting-button'}
                    >
                        {t('thematicMeetings.plan.reopenMeeting')}
                    </ButtonGradientSecondaryToPrimary>
                </>
            ) : (
                <ButtonGradientSecondaryToPrimary
                    onClick={() => void handleComplete()}
                    loading={isSavingEdits || isCompleting}
                    shape={'pill'}
                    fitContent
                    wrap
                    leftIcon={
                        <Icon
                            name={'essential-tick-circle'}
                            color={Colors.WHITE}
                            ariaHidden
                        />
                    }
                    dataTestId={'mark-completed-button'}
                >
                    {t('thematicMeetings.plan.markCompleted')}
                </ButtonGradientSecondaryToPrimary>
            )}
        </MeetingCompletionCard>
    );
};
